import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';


const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { state, toggleCart } = useCart();
  const headerRef = useRef<HTMLElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keep a CSS variable with header height so the page can offset content
  useEffect(() => {
    const setHeaderHeight = () => {
      const h = headerRef.current ? (headerRef.current as HTMLElement).offsetHeight : 64;
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    };

    setHeaderHeight();
    window.addEventListener('resize', setHeaderHeight);
    return () => window.removeEventListener('resize', setHeaderHeight);
  }, []);

  // Close menu on Escape and allow click-outside to close overlay
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) setIsMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMenuOpen]);

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Shop', href: '/shop', isRoute: true },
    { name: 'Mini Sales', href: '/mini-sales', isRoute: true },
    { name: 'Project Rebuilds', href: '/project-rebuilds', isRoute: true },
    { name: 'Auctions', href: '/auctions', isRoute: true },
    { name: 'Matric Hire', href: '/matric-dance', isRoute: true },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href: string) => {
    if (location.pathname !== '/') {
      // Navigate home, then poll for the target element and scroll when available.
      navigate('/');
      let attempts = 0;
      const tryScroll = () => {
        attempts += 1;
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setIsMenuOpen(false);
        } else if (attempts < 12) {
          // retry up to ~1.2s
          setTimeout(tryScroll, 100);
        } else {
          // give up after retries
          setIsMenuOpen(false);
        }
      };
      // Start after a short delay to allow the new route to render
      setTimeout(tryScroll, 120);
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setIsMenuOpen(false);
    }
  };

  const handleNavClick = (item: any) => {
    if (item.isRoute) {
      navigate(item.href);
      setIsMenuOpen(false);
    } else {
      scrollToSection(item.href);
    }
  };

  return (
    <motion.header
      ref={headerRef as any}
      className={
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md shadow-lg'
      }
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <nav className="container-custom relative">
        <div className="flex items-center justify-between py-2.5 px-3 sm:px-4">
          {/* Logo */}
          <motion.div
            className="flex items-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Compact title for small screens */}
            <div className="sm:hidden">
              <h1 className="text-base font-bold text-mini-red tracking-tight leading-tight">Gerrie's Classic Minis</h1>
              <p className="text-[10px] text-gray-600 -mt-0.5">Keeping Classic Minis Alive</p>
            </div>

            {/* Desktop title */}
            <div className="hidden sm:block">
              <h1 className="text-xl md:text-2xl lg:text-2xl font-heading font-extrabold text-mini-red tracking-tight leading-tight">
                Gerrie's Classic Minis
              </h1>
              <p className="text-xs md:text-sm text-gray-600 font-medium -mt-0.5">
                Keeping Classic Minis Alive
              </p>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-5">
            {navItems.map((item) => (
              <motion.button
                key={item.name}
                onClick={() => handleNavClick(item)}
                className="text-sm text-gray-700 hover:text-mini-red font-medium transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {item.name}
              </motion.button>
            ))}
            
            {/* Cart Icon */}
            <motion.button
              onClick={toggleCart}
              className="relative p-1.5 text-gray-700 hover:text-mini-red transition-colors duration-200"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ShoppingCart size={20} />
              {state.totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-mini-red text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                  {state.totalItems}
                </span>
              )}
            </motion.button>
          </div>

          {/* Mobile Menu */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Mobile Cart Icon */}
            <motion.button
              onClick={toggleCart}
              className="lg:hidden relative p-2 text-gray-700 hover:text-mini-red transition-colors duration-200"
              aria-label="Open cart"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ShoppingCart size={20} />
              {state.totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-mini-red text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold text-[10px]">
                  {state.totalItems}
                </span>
              )}
            </motion.button>
            
            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-lg bg-gray-100 text-gray-700"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          ref={overlayRef as any}
          onClick={(e) => {
            // if clicking the overlay background (not inner content), close
            if (e.target === overlayRef.current) setIsMenuOpen(false);
          }}
          className={`fixed top-0 left-0 right-0 z-[100] lg:hidden overflow-hidden ${isMenuOpen ? 'max-h-[100vh]' : 'max-h-0'} transition-all duration-300`}
          style={{ background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(8px)' }}
          initial={false}
          animate={{ height: isMenuOpen ? 'auto' : 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Visible close button inside overlay so users can always dismiss the menu */}
          <div className="absolute top-3 right-3 z-50">
            <button
              onClick={() => setIsMenuOpen(false)}
              aria-label="Close menu"
              className="p-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="pt-20 pb-6 px-4 space-y-3">
            {navItems.map((item) => (
              <motion.button
                key={item.name}
                onClick={() => {
                  if (item.isRoute) {
                    navigate(item.href);
                    setIsMenuOpen(false);
                  } else {
                    scrollToSection(item.href);
                  }
                }}
                className="block w-full text-left px-4 py-4 text-lg font-semibold text-gray-700 hover:text-mini-red hover:bg-gray-100 rounded-lg transition-colors duration-200"
                whileHover={{ x: 10 }}
              >
                {item.name}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </nav>
    </motion.header>
  );
};

export default Header;
